/* ═══════════════════════════════════════════════════════════════
   ExpenseService — Expense reports CRUD via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { Expense, ExpenseAttachment } from '../../shared/types';
import { getCurrentFirmId } from './firmContext';
import * as cache from './cloudCache';
import * as cloudApi from './apiClient';

// Captured at module load — works only because main.tsx imports webApiShim
// before any service module, so window.api already exists (import-order contract).
const api = (window as any).api;

export const ExpenseService = {
  list: async (memberId?: string, month?: string, status?: string): Promise<Expense[]> => {
    const filters: Record<string, any> = {};
    if (memberId) filters.teamMemberId = memberId;
    if (month) filters.month = month;
    if (status) filters.status = status;
    if (Object.keys(filters).length > 0) {
      return cache.query<Expense>('expenses', filters);
    }
    return cache.list<Expense>('expenses');
  },

  /** Widgets R2 Builder R2 — the Expenses source's project scoping (a
   *  widget lives on ONE project's dashboard, unlike the Expenses tab's
   *  per-member switcher). Same cache the tab reads: the server already
   *  restricts what lands in it to the caller's expenses.view_own/team/all
   *  tier, so no extra client-side scoping is layered here — mirrors the
   *  Expenses page exactly (it does none either; the tab's member picker
   *  is a display choice, not a security boundary). */
  listByProject: async (projectId: string): Promise<Expense[]> =>
    cache.query<Expense>('expenses', { projectId }),

  save: async (e: Expense): Promise<Expense> => cache.save('expenses', e),

  delete: async (id: string): Promise<void> => cache.remove('expenses', id),

  /** Tranche 0 item 2/7: 'approved' now routes through the dedicated
   *  expense_approve server action (permission-gated on expenses.approve,
   *  CALLER stamped server-side) — the generic update path rejects this
   *  transition now. Submit/unsubmit (draft <-> submitted) are unaffected
   *  and stay on the generic path (owner self-service). */
  updateStatus: async (ids: string[], status: string, performedBy?: string): Promise<void> => {
    if (status === 'approved') {
      await cloudApi.expenseApprove(ids);
      cache.invalidate('expenses');
      return;
    }
    const firmId = getCurrentFirmId();
    const now = new Date().toISOString();
    const updates: Record<string, any> = { status, updated_at: now };
    if (status === 'submitted') updates.submitted_at = now;

    await cloudApi.dbUpdate('expenses', updates, [
      { col: 'firm_id', op: '=', val: firmId },
      { col: 'id', op: 'IN', val: ids },
    ]);
    cache.invalidate('expenses');
  },

  /** New (Tranche 0 item 2 groundwork) — rejects submitted expenses with a
   *  reason, stamping the CALLER server-side. No current UI calls this
   *  yet; expenses.reject already exists in the permission catalog. */
  rejectExpenses: async (ids: string[], reason?: string): Promise<void> => {
    await cloudApi.expenseReject(ids, reason);
    cache.invalidate('expenses');
  },

  // Attachments
  listAttachments: async (expenseId: string): Promise<ExpenseAttachment[]> => {
    return cache.query<ExpenseAttachment>('expense_attachments', { expenseId });
  },

  /** Time & Expense rebuild T4: metadata-only attachment list — never pulls
   *  the base64 `data` column. Uses the server's column-projection support
   *  (`dbList(..., columns)`, already wired for exactly this "avoid huge
   *  blobs" case) so the Expenses tab can show file names/counts without
   *  loading every receipt's bytes on page open. Bypasses cloudCache's
   *  generic query cache on purpose — this is a small, cheap read and the
   *  full-row `expense_attachments` cache entries (keyed by filter) must
   *  never get contaminated with a partial-column result. */
  listAttachmentsMeta: async (
    expenseId: string
  ): Promise<Array<Omit<ExpenseAttachment, 'data'>>> => {
    const firmId = getCurrentFirmId();
    const rows = await cloudApi.dbList(
      'expense_attachments',
      firmId,
      { expense_id: expenseId },
      undefined,
      ['id', 'expense_id', 'file_name', 'mime_type', 'created_at']
    );
    return (rows || []).map((r: any) => ({
      id: r.id,
      expenseId: r.expense_id,
      fileName: r.file_name,
      mimeType: r.mime_type,
      createdAt: r.created_at,
    }));
  },

  /** Fetches ONE attachment's full row (incl. base64 `data`) — call only
   *  when the user actually clicks download for that specific file, never
   *  eagerly. */
  getAttachmentData: async (attachmentId: string): Promise<ExpenseAttachment | null> => {
    const firmId = getCurrentFirmId();
    const rows = await cloudApi.dbList('expense_attachments', firmId, { id: attachmentId });
    const r = rows && rows[0];
    if (!r) return null;
    return {
      id: r.id,
      expenseId: r.expense_id,
      fileName: r.file_name,
      mimeType: r.mime_type,
      data: r.data,
      createdAt: r.created_at,
    };
  },

  saveAttachment: async (att: ExpenseAttachment): Promise<ExpenseAttachment> => {
    return cache.save('expense_attachments', att);
  },

  deleteAttachment: async (id: string): Promise<void> => cache.remove('expense_attachments', id),

  // File dialog — stays as IPC (needs main process for native dialog)
  openFileDialog: async (): Promise<{
    data: string;
    fileName: string;
    mimeType: string;
  } | null> => {
    return api.openExpenseFileDialog();
  },
};
